import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { AddCardModalComponent } from './modals/add-card-modal.component';
import { ConfirmationActionComponent } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.component';
import { ConfirmationActionTypes } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.types';
import { BillingService } from 'app/services/billing/billing.service';
import { PaymentMethodCard } from 'app/services/billing/billing.type';
import { generalConsts } from 'app/services/consts/general.consts';

@Component({
    selector: 'payment-methods',
    templateUrl: './payment-methods.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule
    ]
})
export class PaymentMethodsComponent implements OnInit {
    paymentMethods: PaymentMethodCard[] = [];
    loading: boolean = false;

    confirmationDeletePaymentMethodDataObject: ConfirmationActionTypes = {
        title: generalConsts.modules.user.billing.dialogs.confirmationDeletePaymentMethodDataObject.title,
        type: generalConsts.application.dialogs.type.confirmation,
        closeable: true,
        yesButton: { use: true },
        noButton: { use: true },
        cancelButton: { use: false },
        message: generalConsts.modules.user.billing.dialogs.confirmationDeletePaymentMethodDataObject.message,
    };

    constructor(
        private _matDialog: MatDialog,
        private _billingService: BillingService
    ) {}

    ngOnInit(): void {
        this.loadPaymentMethods();
    }

    loadPaymentMethods(): void {
        this.loading = true;
        this._billingService.getPaymentMethods().subscribe({
            next: (response) => {
                this.paymentMethods = response.data;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    getBrandName(brand: string): string {
        const brands: { [key: string]: string } = {
            'visa': 'Visa',
            'mastercard': 'Mastercard',
            'amex': 'American Express',
            'elo': 'Elo',
            'hipercard': 'Hipercard'
        };
        return brands[brand.toLowerCase()] || brand;
    }

    getBrandColor(brand: string): { bg: string; text: string } {
        const colors: { [key: string]: { bg: string; text: string } } = {
            'visa': { bg: 'bg-blue-50', text: 'text-blue-600' },
            'mastercard': { bg: 'bg-orange-50', text: 'text-orange-600' },
            'amex': { bg: 'bg-indigo-50', text: 'text-indigo-600' },
            'elo': { bg: 'bg-yellow-50', text: 'text-yellow-600' },
            'hipercard': { bg: 'bg-red-50', text: 'text-red-600' }
        };
        return colors[brand.toLowerCase()] || { bg: 'bg-gray-50', text: 'text-gray-600' };
    }

    formatExpiry(month: number, year: number): string {
        return `${month.toString().padStart(2, '0')}/${year}`;
    }

    openAddCardModal(): void {
        const dialogRef = this._matDialog.open(AddCardModalComponent, {
            autoFocus: false,
            disableClose: false,
            width: '95%',
            maxWidth: '450px',
            panelClass: 'custom-modal-container'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.action === 'saved') {
                this.loadPaymentMethods();
            }
        });
    }

    confirmDeletePaymentMethod(card: PaymentMethodCard): void {
        if (card.is_default) {
            return;
        }

        const confirmationDialogRef = this._matDialog.open(ConfirmationActionComponent, {
            autoFocus: false,
            data: {
                data: this.confirmationDeletePaymentMethodDataObject,
            },
            disableClose: true,
            width: '95%',
            maxWidth: '500px',
        });

        confirmationDialogRef.afterClosed().subscribe(result => {
            if (result && result.action === generalConsts.modules.user.billing.dialogs.actions.yes) {
                this.deletePaymentMethod(card);
            }
        });
    }

    deletePaymentMethod(card: PaymentMethodCard): void {
        this.loading = true;
        this._billingService.deletePaymentMethod(card.id)
            .subscribe({
                next: () => {
                    this.loadPaymentMethods();
                },
                error: () => {
                    this.loading = false;
                }
            });
    }
}
